/**
 * Order lifecycle helpers shared by the counter and server surfaces.
 *
 * Mirrors `orderAcceptsNewItems` in backend/internal/handlers/kot.go — keep the
 * two in sync. The backend is the authority (it holds the row lock); this copy
 * exists so the UI can drop a dead order proactively instead of only reacting
 * to a 409.
 */

/** Statuses an order can never come back from. Nothing may be appended to these. */
export const TERMINAL_ORDER_STATUSES = ['completed', 'cancelled'] as const

/**
 * True when an order is finished (paid out or cancelled) and must not receive
 * new lines or fire another KOT.
 *
 * F-CTRL-N30 (Chunkyard order 20260729-021, 2026-07-29): voiding the last line
 * of an order makes the backend auto-cancel it. The counter kept holding that
 * cancelled order as its active `continuingOrderId`, so the next customer's
 * items were appended to it and fired to the kitchen. The order stayed
 * cancelled with no payment row, so the food went out and the sale never
 * reached sales, the Z-report, or Day Close.
 */
export function isTerminalOrderStatus(status: string | null | undefined): boolean {
  if (!status) return false
  return (TERMINAL_ORDER_STATUSES as readonly string[]).includes(status)
}

/**
 * The stable backend error code returned when a write is attempted against a
 * terminal order (409). The counter keys off this to clear its stale
 * active-order reference rather than showing a dead-end error.
 */
export const ORDER_NOT_EDITABLE = 'order_not_editable'
